import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowLeft as PrevArrow, ArrowRight as NextArrow,
  ChevronDown, ChevronRight, Loader2, RefreshCw, Shield,
  Zap, Calendar, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { PHASES, getPhaseProgress, computeAutoPhase } from '../../config/build-checklist';
import BuildStepCheckbox from '../../components/admin/BuildStepCheckbox';
import BuildContextPanel from '../../components/admin/BuildContextPanel';
import BuildNotifyModal from '../../components/admin/BuildNotifyModal';
import AdminNav from '../../components/admin/AdminNav';

// ─── Auth helper ──────────────────────────────────────────────────────────────

function getPassword() {
  return sessionStorage.getItem('admin_password') || '';
}

// ─── Phase status styling ─────────────────────────────────────────────────────

const PHASE_BADGE = {
  queued:            'bg-[#F0EFF4]/10 text-[#F0EFF4]/50',
  setup:             'bg-yellow-500/20 text-yellow-400',
  customization:     'bg-[#7B61FF]/20 text-[#7B61FF]',
  awaiting_delivery: 'bg-blue-500/20 text-blue-400',
  delivery:          'bg-orange-500/20 text-orange-400',
  deployed:          'bg-emerald-500/20 text-emerald-400',
};

const PHASE_LABELS = {
  queued:            'Queued',
  setup:             'Setup',
  customization:     'Customization',
  awaiting_delivery: 'Awaiting Delivery',
  delivery:          'Delivery',
  deployed:          'Deployed',
};

// ─── Collapsible subgroup ─────────────────────────────────────────────────────

const SubgroupSection = ({ subgroup, checklistState, onAction, onNotify, phaseUnlocked, clientId, password }) => {
  const [open, setOpen] = useState(true);
  const steps = subgroup.steps;
  const done = steps.filter(s => {
    const st = checklistState[s.id];
    const status = typeof st === 'object' ? st?.status : st;
    return status === 'done' || status === 'na';
  }).length;
  const allDone = done === steps.length;

  return (
    <div className="mb-2">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-[#F0EFF4]/5 transition-colors group"
      >
        <div className="flex items-center gap-2">
          {open
            ? <ChevronDown className="w-3.5 h-3.5 text-[#F0EFF4]/30" />
            : <ChevronRight className="w-3.5 h-3.5 text-[#F0EFF4]/30" />
          }
          <span className="text-xs font-bold text-[#F0EFF4]/60 uppercase tracking-wider font-data">{subgroup.label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-data ${allDone ? 'text-emerald-400' : 'text-[#F0EFF4]/30'}`}>
            {done}/{steps.length}
          </span>
          {allDone && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
        </div>
      </button>

      {open && (
        <div className="space-y-1 mt-1 ml-5">
          {steps.map(step => {
            const rawState = checklistState[step.id];
            const state = rawState
              ? (typeof rawState === 'object' ? rawState : { status: rawState })
              : null;
            return (
              <BuildStepCheckbox
                key={step.id}
                step={step}
                state={state}
                onAction={onAction}
                onNotify={phaseUnlocked ? onNotify : null}
                disabled={!phaseUnlocked}
                clientId={clientId}
                password={password}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Phase section ────────────────────────────────────────────────────────────

const PhaseSection = ({ phase, checklistState, currentPhase, onAction, onNotify, clientId, password }) => {
  const [open, setOpen] = useState(true);
  const progress = getPhaseProgress(phase.id, checklistState);

  const phaseOrder = ['queued', 'setup', 'customization', 'awaiting_delivery', 'delivery', 'deployed'];
  const phaseToChecklist = { setup: 'setup', customization: 'customization', awaiting_delivery: 'customization', delivery: 'delivery', deployed: 'delivery' };
  const phaseUnlocked = phaseOrder.indexOf(currentPhase) >= phaseOrder.indexOf(phase.id);

  const isActive = currentPhase === phase.id ||
    (phase.id === 'customization' && currentPhase === 'awaiting_delivery') ||
    (phase.id === 'delivery' && ['delivery', 'deployed'].includes(currentPhase));

  return (
    <div className={`bg-[#18181B] border rounded-2xl mb-4 overflow-hidden ${
      isActive ? 'border-[#7B61FF]/30' : phaseUnlocked ? 'border-[#F0EFF4]/10' : 'border-[#F0EFF4]/5 opacity-60'
    }`}>
      {/* Phase header */}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#F0EFF4]/3 transition-colors"
      >
        <div className="flex items-center gap-3">
          {open ? <ChevronDown className="w-4 h-4 text-[#F0EFF4]/40" /> : <ChevronRight className="w-4 h-4 text-[#F0EFF4]/40" />}
          <span className="font-bold text-[#F0EFF4]">{phase.label}</span>
          {isActive && (
            <span className="text-[10px] font-data font-bold px-1.5 py-0.5 rounded bg-[#7B61FF]/20 text-[#7B61FF]">ACTIVE</span>
          )}
          {!phaseUnlocked && (
            <span className="text-[10px] font-data text-[#F0EFF4]/20">Locked</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Progress bar */}
          <div className="w-24 h-1.5 bg-[#F0EFF4]/10 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                progress.pct === 100 ? 'bg-emerald-500' : progress.pct > 0 ? 'bg-[#7B61FF]' : 'bg-transparent'
              }`}
              style={{ width: `${progress.pct}%` }}
            />
          </div>
          <span className={`text-xs font-data ${progress.pct === 100 ? 'text-emerald-400' : 'text-[#F0EFF4]/40'}`}>
            {progress.done}/{progress.total}
          </span>
        </div>
      </button>

      {/* Subgroups */}
      {open && (
        <div className="px-4 pb-4 border-t border-[#F0EFF4]/5 pt-3">
          {phase.subgroups.map(sg => (
            <SubgroupSection
              key={sg.id}
              subgroup={sg}
              checklistState={checklistState}
              onAction={onAction}
              onNotify={onNotify}
              phaseUnlocked={phaseUnlocked}
              clientId={clientId}
              password={password}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── BuildSheet ───────────────────────────────────────────────────────────────

const BuildSheet = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const password = getPassword();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notifyStep, setNotifyStep] = useState(null);
  const [allBuilds, setAllBuilds] = useState([]);
  const [updating, setUpdating] = useState(false);

  // Detect temp workarounds from checklist state
  const getTempWorkarounds = (cs) => {
    if (!cs) return [];
    const items = [];
    const check = (stepId, label, action) => {
      const s = cs[stepId];
      const status = typeof s === 'object' ? s?.status : s;
      if (status === 'na') items.push({ step_id: stepId, label, action });
    };
    check('preflight_apple_id', 'Apple ID', 'Client needs to create their own Apple ID and transfer ownership');
    check('preflight_agent_email', 'Agent email', 'Client needs a permanent agent email address set up');
    check('preflight_telegram_bot', 'Telegram bot', 'Client needs their own bot token created via @BotFather');
    return items;
  };

  const channelRef = useRef(null);

  // ─── Fetch sheet data ────────────────────────────────────────────────────────

  const fetchSheet = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/get-build-sheet?id=${id}`, {
        headers: { 'x-admin-password': password },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, password]);

  // ─── Fetch board for nav ─────────────────────────────────────────────────────

  const fetchBoard = useCallback(async () => {
    try {
      const res = await fetch('/api/get-build-board', {
        headers: { 'x-admin-password': password },
      });
      const json = await res.json();
      if (res.ok) setAllBuilds(json.builds || []);
    } catch {}
  }, [password]);

  // ─── Reset transient state when navigating to a different build ─────────────

  useEffect(() => {
    setNotifyStep(null);
  }, [id]);

  // ─── Realtime ────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchSheet();
    fetchBoard();

    const channel = supabase
      .channel(`build-sheet-${id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'build_packages', filter: `id=eq.${id}` }, () => {
        fetchSheet();
      })
      .subscribe();

    channelRef.current = channel;
    return () => supabase.removeChannel(channel);
  }, [id, fetchSheet, fetchBoard]);

  // ─── Step action ─────────────────────────────────────────────────────────────

  const handleAction = useCallback(async (stepId, action, extra = {}) => {
    if (!data?.build) return;
    setUpdating(true);
    try {
      const res = await fetch('/api/update-checklist-step', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
        body: JSON.stringify({
          build_package_id: data.build.id,
          step_id: stepId,
          action,
          ...extra,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Update failed');
      // Optimistic update of checklist_state and phase
      setData(prev => ({
        ...prev,
        build: {
          ...prev.build,
          checklist_state: json.build.checklist_state,
          phase: json.build.phase,
        },
      }));
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  }, [data, password]);

  // ─── Phase override ──────────────────────────────────────────────────────────

  const handlePhaseChange = async (phase) => {
    if (!data?.build) return;
    try {
      const res = await fetch('/api/update-build-phase', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
        body: JSON.stringify({ build_package_id: data.build.id, phase }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      setData(prev => ({ ...prev, build: { ...prev.build, phase: json.build.phase } }));
    } catch (err) {
      alert(err.message);
    }
  };

  // ─── Navigation ──────────────────────────────────────────────────────────────

  const currentIdx = allBuilds.findIndex(b => b.id === id);
  const prevBuild = currentIdx > 0 ? allBuilds[currentIdx - 1] : null;
  const nextBuild = currentIdx < allBuilds.length - 1 ? allBuilds[currentIdx + 1] : null;

  // ─── Redirect to login if no password ────────────────────────────────────────

  if (!password) {
    navigate('/admin/build');
    return null;
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#0A0A14] text-[#F0EFF4] flex flex-col">
      {/* Top nav */}
      <div className="border-b border-[#F0EFF4]/10 px-6 py-3 space-y-3 bg-[#0A0A14] sticky top-0 z-10">
        <AdminNav />
        <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/build')}
          className="flex items-center gap-1.5 text-[#F0EFF4]/40 hover:text-[#F0EFF4] text-sm transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Board
        </button>

        <div className="flex-1 flex items-center gap-3">
          {data?.build && (
            <>
              <span className="font-bold text-[#F0EFF4]">{data.build.clients?.name || 'Unknown'}</span>
              <span className={`text-xs font-data font-bold px-2 py-0.5 rounded-full ${PHASE_BADGE[data.build.phase] || PHASE_BADGE.queued}`}>
                {PHASE_LABELS[data.build.phase] || data.build.phase}
              </span>
              {/* Phase selector */}
              <select
                value={data.build.phase}
                onChange={e => handlePhaseChange(e.target.value)}
                className="text-xs font-data bg-[#F0EFF4]/5 border border-[#F0EFF4]/10 rounded-lg px-2 py-1 text-[#F0EFF4] outline-none focus:border-[#7B61FF]/50 cursor-pointer"
              >
                {Object.entries(PHASE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </>
          )}
          {updating && <Loader2 className="w-4 h-4 animate-spin text-[#7B61FF]" />}
        </div>

        <div className="flex items-center gap-2">
          {/* Client dropdown */}
          {allBuilds.length > 0 && (
            <select
              value={id}
              onChange={e => navigate(`/admin/build/${e.target.value}`)}
              className="text-xs font-data bg-[#F0EFF4]/5 border border-[#F0EFF4]/10 rounded-lg px-2 py-1 text-[#F0EFF4] outline-none focus:border-[#7B61FF]/50 cursor-pointer max-w-40"
            >
              {allBuilds.map(b => (
                <option key={b.id} value={b.id}>
                  {b.client?.name || 'Unknown'}
                </option>
              ))}
            </select>
          )}
          {/* Prev/Next */}
          <button
            onClick={() => prevBuild && navigate(`/admin/build/${prevBuild.id}`)}
            disabled={!prevBuild}
            className="p-1.5 rounded-lg hover:bg-[#F0EFF4]/10 text-[#F0EFF4]/40 hover:text-[#F0EFF4] disabled:opacity-20 transition-colors"
            title="Previous build"
          >
            <PrevArrow className="w-4 h-4" />
          </button>
          <button
            onClick={() => nextBuild && navigate(`/admin/build/${nextBuild.id}`)}
            disabled={!nextBuild}
            className="p-1.5 rounded-lg hover:bg-[#F0EFF4]/10 text-[#F0EFF4]/40 hover:text-[#F0EFF4] disabled:opacity-20 transition-colors"
            title="Next build"
          >
            <NextArrow className="w-4 h-4" />
          </button>
          <button
            onClick={fetchSheet}
            disabled={loading}
            className="p-1.5 rounded-lg hover:bg-[#F0EFF4]/10 text-[#F0EFF4]/40 hover:text-[#F0EFF4] transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        </div>
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#7B61FF]" />
        </div>
      )}

      {data && (
        <div key={id} className="flex-1 flex overflow-hidden">
          {/* ─── Left: Checklist (60%) ─────────────────────────────────────── */}
          <div className="w-[60%] overflow-y-auto px-6 py-5 border-r border-[#F0EFF4]/10">
            {/* Temp workaround banner */}
            {(() => {
              const tempItems = getTempWorkarounds(data.build.checklist_state);
              if (tempItems.length === 0) return null;
              return (
                <div className="mb-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" />
                    <span className="font-bold text-amber-400 text-sm">
                      {tempItems.length} Temporary Workaround{tempItems.length > 1 ? 's' : ''}
                    </span>
                    <span className="text-xs text-amber-400/60 font-data">Resolve before delivery</span>
                  </div>
                  <div className="space-y-1.5 ml-7">
                    {tempItems.map(item => (
                      <div key={item.step_id} className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                        <div>
                          <span className="text-sm text-[#F0EFF4]/80 font-semibold">{item.label}</span>
                          <span className="text-xs text-[#F0EFF4]/40 ml-2">{item.action}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Config gate banner between Phase 1 → 2 */}
            {data.build.phase === 'customization' && !data.build.config_generated_at && (
              <div className="mb-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-400 shrink-0" />
                <div>
                  <div className="font-bold text-yellow-400 text-sm">Config files not yet generated</div>
                  <div className="text-xs text-yellow-400/70 mt-0.5">
                    Generate configs from the Dossier before completing Phase 2 steps.
                  </div>
                </div>
              </div>
            )}

            {/* Delivery call scheduler */}
            {data.build.phase === 'awaiting_delivery' && (
              <div className="mb-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center gap-3">
                <Calendar className="w-5 h-5 text-blue-400 shrink-0" />
                <div className="flex-1">
                  <div className="font-bold text-blue-400 text-sm">Schedule Delivery Call</div>
                  <div className="text-xs text-blue-400/70 mt-0.5">Set date to advance to Delivery phase.</div>
                </div>
                <input
                  type="datetime-local"
                  defaultValue={data.build.delivery_call_scheduled_at?.slice(0, 16) || ''}
                  onChange={async (e) => {
                    try {
                      const res = await fetch('/api/update-build-phase', {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
                        body: JSON.stringify({
                          build_package_id: data.build.id,
                          phase: data.build.phase,
                          delivery_call_scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : null,
                        }),
                      });
                      const json = await res.json();
                      if (res.ok) {
                        setData(prev => ({ ...prev, build: { ...prev.build, ...json.build } }));
                      }
                    } catch {}
                  }}
                  className="text-xs font-data bg-[#F0EFF4]/5 border border-blue-500/30 rounded-lg px-2 py-1.5 text-[#F0EFF4] outline-none focus:border-blue-500/60"
                />
              </div>
            )}

            {/* Phase sections */}
            {PHASES.map(phase => (
              <PhaseSection
                key={phase.id}
                phase={phase}
                checklistState={data.build.checklist_state || {}}
                currentPhase={data.build.phase}
                onAction={handleAction}
                onNotify={setNotifyStep}
                clientId={data.build.client_id}
                password={password}
              />
            ))}
          </div>

          {/* ─── Right: Context panel (40%) ───────────────────────────────── */}
          <div className="w-[40%] overflow-y-auto px-5 py-5">
            <BuildContextPanel
              build={data.build}
              intake={data.intake}
              credentials={data.credentials}
              documents={data.documents}
            />
          </div>
        </div>
      )}

      {/* Notify modal */}
      {notifyStep && data?.build && (
        <BuildNotifyModal
          step={notifyStep}
          buildId={data.build.id}
          clientEmail={data.build.clients?.email}
          password={password}
          onClose={() => setNotifyStep(null)}
          onSent={fetchSheet}
        />
      )}
    </div>
  );
};

export default BuildSheet;
